import { memo, useCallback, useEffect, useId, useRef } from 'react';
import cn from 'classnames';
import type { PlayerInitParams, VideoPlayerInstance } from './lib/types';
import { useVideoPlayer } from './lib/useVideoPlayer';
import styles from './VideoPlayer.module.css';

export interface VideoPlayerProps {
    /** URL видео для воспроизведения */
    videoUrls: string[];
    /** URL превью изображения (первый кадр видео) */
    posterUrl?: string;
    /** Автоматическое воспроизведение */
    autoplay?: boolean;
    /** Отключение звука */
    muted?: boolean;
    /** Отключение скина плеера (только видео без контролов) */
    skinless?: boolean;
    /** Скрытие контролов плеера ('*' для всех контролов) */
    hiddenControls?: string;
    /** CSS класс для контейнера */
    className?: string;
    /** Стили для контейнера */
    style?: React.CSSProperties;
    /** Колбэк при готовности плеера */
    onReady?: (player: VideoPlayerInstance) => void;
    /** Колбэк при ошибке */
    onError?: (error: unknown) => void;
    /** Версия плеера */
    version?: string;
    /** CSP nonce для скрипта */
    cspNonce?: string;
}

function GrayFallback({ className, style }: { className?: string; style?: React.CSSProperties }) {
    return <div className={cn(styles.fallback, className)} translate="no" style={style} />;
}

export const VideoPlayer = memo(function VideoPlayer({
    videoUrls,
    posterUrl,
    autoplay = false,
    muted = false,
    skinless = false,
    hiddenControls,
    className,
    style,
    onReady,
    onError,
    version,
    cspNonce,
}: VideoPlayerProps) {
    const { status, createPlayer, error } = useVideoPlayer({ version, cspNonce });
    const containerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<VideoPlayerInstance | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const playerId = useId();

    const handleError = useCallback(
        (error: unknown) => {
            onError?.(error);
        },
        [onError]
    );

    const handleReady = useCallback(
        (player: VideoPlayerInstance) => {
            onReady?.(player);
        },
        [onReady]
    );

    const cleanup = useCallback(() => {
        if (playerRef.current) {
            playerRef.current.destroy();
            playerRef.current = null;
        }

        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error, handleError]);

    useEffect(() => {
        if (!containerRef.current || !videoUrls || !createPlayer || status !== 'READY') {
            return;
        }

        containerRef.current.id = playerId;

        const initPlayer = () => {
            const element = document.getElementById(playerId);

            if (!element) {
                handleError(new Error('Container element not found'));

                return;
            }

            try {
                const skinParams = hiddenControls
                    ? {
                          skinConfig: {
                              hiddenControls,
                          },
                      }
                    : undefined;

                const playerParams: PlayerInitParams = {
                    element: playerId,
                    source: {
                        streams: videoUrls.map((url) => {
                            return { url };
                        }),
                        withCredentials: false,
                        ...(posterUrl && { preview: posterUrl }),
                    },
                    ...(autoplay && { autoplay }),
                    ...(muted && { muted }),
                    ...(version && { version }),
                    ...(skinless && { loadSkin: false as const }),
                    ...(skinParams && { skinParams }),
                };

                const player = createPlayer(playerParams);

                playerRef.current = player;

                player.once('Ready', () => {
                    handleReady(player);

                    if (containerRef.current && !observerRef.current) {
                        observerRef.current = new IntersectionObserver(
                            (entries) => {
                                entries.forEach((entry) => {
                                    // Если видео видно менее чем на 50%, ставим на паузу
                                    if (entry.intersectionRatio < 0.5 && playerRef.current) {
                                        playerRef.current.pause();
                                    }
                                });
                            },
                            {
                                threshold: [0, 0.5, 1],
                            }
                        );

                        observerRef.current.observe(containerRef.current);
                    }
                });

                player.on('Error', (error) => {
                    console.error('VideoPlayer: Player error:', error);
                    handleError(error);
                });
            } catch (error) {
                handleError(error);
            }
        };

        requestAnimationFrame(initPlayer);

        return cleanup;
    }, [
        videoUrls,
        posterUrl,
        autoplay,
        muted,
        skinless,
        hiddenControls,
        version,
        createPlayer,
        status,
        playerId,
        cleanup,
        handleError,
        handleReady,
    ]);

    if (status === 'NOT_INITIALIZED' || status === 'LOADING') {
        return <GrayFallback className={className} style={style} />;
    }

    if (status === 'ERROR') {
        return null;
    }

    if (status !== 'READY' || !createPlayer) {
        return null;
    }

    return <div ref={containerRef} className={cn(styles.container, className)} translate="no" style={style} />;
});
